// FUNCTIONALITY-115C-R2 — EN overlays for store/legal SoT (same section keys as UA).
import type { InfoSection, LegalSectionKind } from "./store-info-v2.js";
import { PAYMENT_METHODS_EN, STORE_INFO } from "./store-info-v2.js";

export const BONUS_RULES_VERSION = "referral-115a-v1";
export const BONUS_RULES_EFFECTIVE_DATE = "2026-07-17";

export const BONUS_REFERRAL_RULES_UA: InfoSection = {
  kind: "bonuses",
  title: "Бонуси та реферальна програма",
  body: [
    "Це персональна маркетингова знижка, а не гроші, не переказ і не виплата. Її не можна передати іншій особі або вивести.",
    "Що отримає друг: 3% на перше оплачене й успішно виконане замовлення.",
    "Що отримаєте ви: 3% на наступну покупку після виконання замовлення друга (після anti-fraud hold).",
    "Мінімальна сума товарів без доставки: 700 ₴. Максимальна вигода за одне замовлення: 150 ₴. Термін дії після активації: 30 днів.",
    "Доставка, послуги перевізника, оптові та дропшипінг-ціни не враховуються. Знижки за реферальною програмою не сумуються з іншими акціями, бонусами та промокодами, крім випадків, коли можливість сумування прямо зазначена в умовах відповідної пропозиції. Якщо доступно декілька несумісних знижок, система автоматично застосує найвигіднішу для покупця. Максимальна сумарна знижка: 10%.",
    "Бонус не активується одразу після натискання «Поділитися». Винагорода активується лише після завершення першого оплаченого й успішно виконаного замовлення друга та проходження налаштованого anti-fraud hold.",
    "Бонус не активується або скасовується, якщо замовлення друга скасовано, не отримано, повернено, визнано шахрайським, є self-referral або дублікатом акаунта.",
    "Після активації промо не погіршується до закінчення терміну дії, окрім випадків повернення/шахрайства, описаних у цих правилах.",
  ],
};

export const BONUS_REFERRAL_RULES_EN: InfoSection = {
  kind: "bonuses",
  title: "Bonuses and referral program",
  body: [
    "This is a personal marketing discount, not cash, not a transfer and not a withdrawal. It cannot be transferred to another person or withdrawn.",
    "What your friend gets: 3% on the first paid and successfully fulfilled order.",
    "What you get: 3% on your next purchase after their order is fulfilled (after anti-fraud hold).",
    "Minimum merchandise subtotal: 700 UAH. Maximum benefit per order: 150 UAH. Validity after activation: 30 days.",
    "Delivery, carrier fees, wholesale and dropshipping prices are excluded. Referral discounts do not stack with other promotions, bonuses or promo codes unless the relevant offer explicitly states that stacking is allowed. If several incompatible discounts are available, the system automatically applies the option with the highest customer savings. Maximum combined discount: 10%.",
    "The bonus is not activated immediately after pressing Share. The reward activates only after the friend's first paid and successfully fulfilled order is completed and survives the configured anti-fraud hold.",
    "The bonus is not activated or is cancelled when the referred order is cancelled, not received, refunded/returned, fraudulent, self-referral, or a duplicate account.",
    "An already activated promo is not worsened before expiry, except for refund/fraud conditions already disclosed in these rules.",
  ],
};

const EN_SECTIONS: Record<LegalSectionKind, InfoSection> = {
  about: {
    kind: "about",
    title: "About the store and seller",
    body: [
      `${STORE_INFO.name} is a store of authentic Asian flavours.`,
      "Taste Asia for real!",
      "Business hours: Tue–Sun, 09:00–18:00. Monday — closed.\nOrders received after 18:00 or on a day off are processed when possible; otherwise on the next business day.",
    ],
  },
  offer: {
    kind: "offer",
    title: "Public offer",
    body: [],
  },
  privacy: {
    kind: "privacy",
    title: "Privacy policy",
    body: [],
  },
  payment: {
    kind: "payment",
    title: "Payment and delivery",
    body: [
      "Delivery is provided by Nova Poshta across Ukraine under the carrier’s current terms and tariffs.",
      "Delivery cost and cash-on-delivery or money-transfer fees are not included in the product price unless explicitly stated at checkout.",
      "After paying by FOP transfer, upload the payment receipt and send it to the manager in the app.",
    ],
    paymentMethods: PAYMENT_METHODS_EN,
    links: [
      { label: "Nova Poshta tariffs", href: "https://novaposhta.ua/shipping-cost/" },
      {
        label: "Cash on delivery / money transfer",
        href: "https://novaposhta.ua/financial-services/money-transfer/to-sender/",
      },
    ],
  },
  returns: {
    kind: "returns",
    title: "Returns and exchange",
    body: [
      "Food products of proper quality cannot be exchanged or returned under the non-returnable goods list. If the product is damaged, of improper quality or does not match the order, contact the manager immediately after receipt.",
    ],
    links: [
      {
        label: "Law of Ukraine On consumer rights protection, Article 9",
        href: "https://zakon.rada.gov.ua/laws/show/1023-12#Text",
      },
      {
        label: "CMU Resolution No.1243, non-returnable goods list, Food products",
        href: "https://zakon.rada.gov.ua/laws/show/1243-2024-%D0%BF#Text",
      },
    ],
  },
  food: {
    kind: "food",
    title: "Food product safety and quality",
    body: [
      "We comply with Ukrainian food safety and quality requirements.",
      "Products are stored and shipped according to manufacturer storage conditions.",
      "Food products of proper quality cannot be exchanged or returned under the non-returnable goods list. If the product is damaged, of improper quality or does not match the order, contact the manager immediately after receipt.",
    ],
    links: [
      {
        label: "Law of Ukraine On consumer rights protection, Article 9",
        href: "https://zakon.rada.gov.ua/laws/show/1023-12#Text",
      },
      {
        label: "CMU Resolution No.1243, non-returnable goods list, Food products",
        href: "https://zakon.rada.gov.ua/laws/show/1243-2024-%D0%BF#Text",
      },
    ],
  },
  bonuses: BONUS_REFERRAL_RULES_EN,
};

export function localizeInfoSection(section: InfoSection, lang: "ua" | "en"): InfoSection {
  if (lang === "ua") {
    if (section.kind === "bonuses") return BONUS_REFERRAL_RULES_UA;
    return section;
  }
  const kind = section.kind;
  if (!kind || kind === "contacts") return section;
  return EN_SECTIONS[kind] ?? section;
}

export function localizeInfoSections(sections: InfoSection[], lang: "ua" | "en"): InfoSection[] {
  return sections.map((section) => localizeInfoSection(section, lang));
}

export const CONSENT_SHORT_EN = {
  title: "Before you start",
  body: [
    "Please review the terms for placing orders and processing personal data.",
  ],
};

/** Known UA-only paragraphs that must not appear on EN legal/about surfaces. */
export const UA_ONLY_LEGAL_MARKERS = [
  "Продавець:",
  "Політика конфіденційності",
  "Оплата і доставка",
  "Безпечність та якість харчових продуктів",
  "Бонуси та реферальна програма",
  "Після переказу натисніть",
  "Скопіюйте вручну",
  "ЄДРПОУ / ІПН",
];
